import * as turf from "@turf/turf";
import {
  BBox,
  Feature,
  FeatureCollection,
  LineString,
  MultiLineString,
  MultiPolygon,
  Point,
  Polygon,
  Position
} from "geojson";
import {GeometryUtil} from "./geometry";


type LocalFeature = Feature<LineString | MultiLineString | Polygon | MultiPolygon>;

export class FeatureUtil {
  static collection(features: Feature[]): FeatureCollection {
    return turf.featureCollection(features);
  }

  static generateMaxBounds(center: number[], radius: number): BBox {
    let circle = turf.circle(center, radius, {units: 'meters'});

    return turf.bbox(circle);
  }

  static reProjectToCanvas(points: Feature<Point>[], canvasPoints: Position[], canvasSize: {x: number, y: number}): Feature<Point>[] {

    let width = canvasPoints[1][0] - canvasPoints[0][0];
    let height = canvasPoints[2][1] - canvasPoints[1][1];

    let widthRatio = width / canvasSize.x;
    let heightRatio = height / canvasSize.y;

    return points.map((featurePoint: Feature<Point>) => {
      let referencePoint = [...canvasPoints[0]];

      let reprojectedPoint: Feature<Point> = JSON.parse(JSON.stringify(featurePoint));

      reprojectedPoint.properties._originalCoordinates = [...featurePoint.geometry.coordinates];

      reprojectedPoint.geometry.coordinates = [
        referencePoint[0] + featurePoint.properties.offsetX * widthRatio,
        referencePoint[1] + featurePoint.properties.offsetY * heightRatio,
      ];

      return reprojectedPoint;
    })
  }

  static extractLineFrom(feature: LocalFeature, startPath: string, endPath: string) {
    let pool = this.featureCoordinatePathPool(feature, startPath);
    let start = Number.parseInt(startPath.split('.').pop());
    let end = Number.parseInt(endPath.split('.').pop());

    let direction = end >= start ? 1 : -1;
    if (feature.geometry.type != 'LineString' && Math.abs(end - start) > pool.length / 2) {
      direction = direction == 1 ? -1 : 1;
    }

    let subPool: Position[] = [], currentIndex = start;
    while (true) {
      subPool.push(pool[currentIndex]);
      if (currentIndex === end) {
        break;
      }
      currentIndex = currentIndex + direction;
      if (direction > 0 && currentIndex === pool.length) {
        currentIndex = 0;
      } else if (direction < 0 && currentIndex === -1) {
        currentIndex = pool.length - 1;
      }
    }

    if (direction > 0) {
      subPool = subPool.slice(1);
    } else {
      subPool = subPool.slice(0, subPool.length - 1);
    }


    return subPool;
  }

  static cleanUpFeature(feature: Feature) {
    switch (feature.geometry.type) {
      case 'LineString':
        feature.geometry.coordinates = this.deduplicateCoordinates(feature.geometry.type, feature.geometry.coordinates);
        break;
      case 'Polygon':
        feature.geometry.coordinates[0] = this.deduplicateCoordinates(feature.geometry.type, feature.geometry.coordinates[0]);
        break;
    }

    return feature;
  }

  static deduplicateCoordinates(geometryType: string, positions: Position[], distanceMargin: number = 0): Position[] {
    let lastPosition = undefined;
    let newPositions: Position[] = [];
    /*let firstPoint = positions[0]
    let lastPoint = positions[positions.length - 1]
    positions.forEach((position, index) => {
      if (index > 0 && index < positions.length - 1) {
        if (position !== firstPoint && position !== lastPoint)
          newPositions.push(position)
      }
      else newPositions.push(position)
    });*/
    /*positions.forEach((position, index) => {
      if (lastPosition) {
        if (distanceMargin > 0 && GeometryUtil.pointsDistance(lastPosition, position) <= distanceMargin) {
          console.log(`Got near-duplicate position at ${index}`, lastPosition);
          return;
        }
        if ((position[0] === lastPosition[0] && position[1] === lastPosition[1])) {
          console.log(`Got duplicate position at ${index}`, lastPosition);
          return;
        }
      }
      newPositions.push(position);
      lastPosition = position;
    });

    if (newPositions[0][0] !== newPositions[newPositions.length - 1][0] || newPositions[0][1] !== newPositions[newPositions.length - 1][1]) {
      newPositions.push(newPositions[0]);
    }*/

      positions.forEach(coord => {
      if (!newPositions.some(c => c[0] === coord[0] && c[1] === coord[1])) {
        newPositions.push(coord);
      }
    });

    switch (geometryType) {
      case 'LineString':
        newPositions = GeometryUtil.rewind(turf.lineString(newPositions))?.geometry?.coordinates[0]
        break;
      case 'Polygon':
        let newPolygon = newPositions.reverse();
        if(newPolygon?.length && newPolygon[0] !== newPolygon[newPolygon.length-1]) newPolygon.push(newPolygon[0]);
        newPositions = GeometryUtil.rewind(turf.polygon([newPolygon]))?.geometry?.coordinates[0]
        break;
    }

    return newPositions;
  }

  static featureCoordinateAtPath(feature: LocalFeature, path: string): Position {
    let pathParts = path.split('.');
    let coordinates = feature.geometry.coordinates;

    let subSet: any = [...coordinates];
    pathParts.forEach(index => subSet = subSet[index]);

    return subSet as Position;
  }

  static featureCoordinatePathPool(feature: LocalFeature, path: string): Position[] {
    let pathParts = path.split('.');
    pathParts.pop();

    let subSet: any = [...feature.geometry.coordinates];

    if (feature.geometry.type === 'LineString') {
      return subSet;
    }

    pathParts.forEach(index => subSet = subSet[index]);

    return subSet;
  }

  static buffer(feature: Feature, radius: number): Feature<Polygon | MultiPolygon> {
    return turf.buffer(feature, radius, {units: 'meters'});
  }

  static lineOffset(feature: Feature<LineString | MultiLineString>, offset: number): Feature<LineString | MultiLineString> {
    return turf.lineOffset(feature, offset, {units: 'meters'});
  }

  /**
   * Decompose a GeoJSON features into two lists of vertex and lines
   */
  static decompose(feature: Feature, parentPath: string | undefined = undefined, skipLastVertex: boolean = false): FeatureComposition {
    let composition = new FeatureComposition(), lastVertex = undefined;

    switch (feature.geometry.type) {
      case "LineString":
        feature.geometry.coordinates.forEach((coordinatePair, index) => {
          if (index === feature.geometry.coordinates.length - 1) {
            return;
          }
          let path = parentPath ? `${parentPath}.${index}` : index.toString();

          composition.vertexMap.set(path, coordinatePair)
        });
        composition.contourLines.set(parentPath ?? '0', this._childLineString(feature, feature.geometry.coordinates));
        break;
      case "MultiLineString":
        feature.geometry.coordinates.forEach((subGeometry, index) => {
          composition.pushChild(this.decompose(this._childLineString(feature, subGeometry), index.toString(), true));
        });
        break;
      case "Polygon":
        feature.geometry.coordinates.forEach((ring, index) => {
          // if (index === (feature.geometry as Polygon).coordinates.length - 1) {
          //   return;
          // }
          let path = parentPath ? `${parentPath}.${index}` : index.toString();
          composition.pushChild(this.decompose(this._childLineString(feature, ring), path, true))
        })
        break;
      case "MultiPolygon":
        feature.geometry.coordinates.forEach((subPolygon, subPolygonIndex) => composition.pushChild(this.decompose(this._childPolygon(feature, subPolygon), subPolygonIndex.toString())));
        break;
      case "Point":
        composition.vertexMap.set('0', feature.geometry.coordinates);
        break;
      case "MultiPoint":
        feature.geometry.coordinates.forEach((pair, index) => {
          composition.vertexMap.set(index.toString(), pair);
        });
        break;
      default:
        throw new Error(`Cannot decompose geometry type ${feature.geometry.type}`);
    }

    return composition;
  }

  private static _childLineString(feature: Feature, vertexList: ([number, number] | Position)[]): Feature<LineString> {
    return turf.lineString(vertexList, undefined, {id: feature.id});
  }

  private static _childPolygon(feature: Feature, vertexList: ([number, number] | Position)[][]): Feature<Polygon> {
    return turf.polygon(vertexList, undefined, {id: feature.id});
  }
}

export class FeatureComposition {
  vertexMap: Map<string, Position> = new Map();
  contourLines: Map<string, Feature<LineString>> = new Map();

  pushChild(subComposition: FeatureComposition): this {

    subComposition.vertexMap.forEach((value, key) => this.vertexMap.set(key, value));
    subComposition.contourLines.forEach((contour, path) => this.contourLines.set(path, contour))

    return this;
  }

  closestVertex(base: Position): {vertex: Position, distance: number, path: string} {
    let minDistance = undefined, minVertex = undefined, minPath = undefined;

    this.vertexMap.forEach((vertex, path) => {
      let distance = GeometryUtil.pointsDistance(base, vertex);
      if (!minDistance || distance < minDistance) {
        minDistance = distance;
        minVertex = vertex;
        minPath = path;
      }
    });

    return {vertex: minVertex, distance: minDistance, path: minPath};
  }

  closestPointOnContour(base: Position): {point: Position, distance: number, path: string} {
    let minDistance = undefined, minPoint = undefined, minPath = undefined;
    this.contourLines.forEach((line, path) => {
      //let projection = turf.nearestPointOnLine(line, base).geometry.coordinates;
      let projection = turf.pointOnLine(line, base).geometry.coordinates;
      let projectionPath = path + '.' + this.pathOfPointOnContour(projection, line);
      let projectionDistance = GeometryUtil.pointsDistance(base, projection);

      if (!minDistance || projectionDistance < minDistance) {
        minDistance = projectionDistance;
        minPoint = projection;
        minPath = projectionPath;
      }
    });

    return {point: minPoint, distance: minDistance, path: minPath};
  }

  pathOfPointOnContour(base: Position, lineString: Feature<LineString>): string {
    let lastPoint = undefined, lastPath = undefined, path = undefined;
    lineString.geometry.coordinates.forEach((coordinatesPair, index) => {
      if (lastPoint) {
        let distance = GeometryUtil.pointToLineDistance(base, [lastPoint, coordinatesPair]);
        if (GeometryUtil.pointsDistance(base, coordinatesPair) === 0) {
          path = index.toString();
        } else if (distance < 3) {
          path = lastPath;
        }
      }
      lastPoint = [...coordinatesPair];
      lastPath = index.toString();
    })

    return path;
  }
}
